import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Icon } from "@/components/Icon";
import { useAppConfig } from "@/lib/use-app-config";

const TITLE = "Politica de confidențialitate — Asistent Auto";
const DESCRIPTION =
  "Cum colectăm, utilizăm și protejăm datele tale personale în aplicația și pe site-ul Asistent Auto.";

export default function ConfidentialitatePage() {
  const { companyName, address, contactEmail } = useAppConfig();

  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path="/confidentialitate" />
      <header className="page-hero">
        <div className="shell">
          <div className="section-eyebrow">Legal</div>
          <h1>
            Politica de<br />
            <em>confidențialitate.</em>
          </h1>
          <p>
            Cum sunt colectate, utilizate și protejate datele tale personale
            când folosești site-ul și aplicația Asistent Auto.
          </p>
        </div>
      </header>

      <section className="section section--tight">
        <div className="shell">
          <div className="legal">
            <aside className="legal-toc">
              <h4>Cuprins</h4>
              <ol>
                <li>
                  <a href="#general">Informații generale</a>
                </li>
                <li>
                  <a href="#colectare">Ce date colectăm?</a>
                  <ol>
                    <li>
                      <a href="#auth">Pentru autentificare</a>
                    </li>
                    <li>
                      <a href="#google">Autentificarea cu Google</a>
                    </li>
                    <li>
                      <a href="#asigurari">Pentru asigurări</a>
                    </li>
                    <li>
                      <a href="#auto">Date colectate automat</a>
                    </li>
                  </ol>
                </li>
                <li>
                  {/* design-check: allow — an anchor id, not a colour */}
                  <a href="#dece">De ce colectăm aceste date?</a>
                </li>
                <li>
                  <a href="#partajare">Cu cine partajăm datele?</a>
                </li>
                <li>
                  <a href="#securitate">Securitate</a>
                </li>
                <li>
                  <a href="#linkuri">Linkuri către alte site-uri</a>
                </li>
                <li>
                  <a href="#stocare">Perioada de stocare</a>
                </li>
                <li>
                  <a href="#drepturi">Drepturile tale</a>
                  <ol>
                    <li>
                      <a href="#d1">Dreptul de a fi informat</a>
                    </li>
                    <li>
                      <a href="#d2">Dreptul de acces</a>
                    </li>
                    <li>
                      <a href="#d3">Dreptul la rectificare</a>
                    </li>
                    <li>
                      <a href="#d4">Dreptul la ștergere</a>
                    </li>
                    <li>
                      <a href="#d5">Dreptul la restricționare</a>
                    </li>
                    <li>
                      <a href="#d6">Portabilitatea datelor</a>
                    </li>
                    <li>
                      <a href="#d7">Dreptul de opoziție</a>
                    </li>
                    <li>
                      <a href="#d8">Decizii automate</a>
                    </li>
                    <li>
                      <a href="#d9">Dreptul de a se adresa justiției</a>
                    </li>
                  </ol>
                </li>
                <li>
                  <a href="#modificari">Modificarea politicii</a>
                </li>
              </ol>
            </aside>

            <article className="prose">
              <h2 id="general">
                <span className="num">1</span>Informații generale
              </h2>
              <p>
                Asistent Auto se angajează să vă protejeze confidențialitatea.
                Politica de confidențialitate explică modul în care
                informațiile dvs. personale sunt colectate, utilizate și
                dezvăluite de Asistent Auto.
              </p>
              <p>
                Această Politică de confidențialitate se aplică site-ului web
                și aplicației Asistent Auto. Accesând sau utilizând serviciul
                nostru, confirmați că ați citit, înțeles și sunteți de acord cu
                colectarea, stocarea, utilizarea și divulgarea informațiilor
                dvs. personale, așa cum este descris în această Politică de
                confidențialitate și în <Link to="/termeni">Termenii și condiții</Link>.
              </p>

              <dl>
                <dt>Operator</dt>
                <dd>{companyName}</dd>
                <dt>Sediul</dt>
                <dd>{address}</dd>
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                </dd>
              </dl>
              <p>
                Puteți contacta responsabilul nostru cu protecția datelor prin
                email la{" "}
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                .
              </p>

              <h2 id="colectare">
                <span className="num">2</span>Ce date colectăm?
              </h2>
              <p>Datele pe care le colectăm și prelucrăm sunt următoarele:</p>

              <h3 id="auth">2.1. Pentru autentificare</h3>
              <p>
                La crearea contului de utilizator în platforma Asistent Auto vă
                solicităm adresa de email și numele. Ne bazăm pe consimțământul
                dvs. pentru furnizarea acestor date cu scopul de a crea contul
                de acces pe platforma noastră și de a verifica autenticitatea
                conturilor create, astfel încât dvs. să puteți beneficia de
                serviciile noastre.
              </p>

              <h3 id="google">2.2. Autentificarea cu Google</h3>
              <p>
                Dacă vă conectați cu contul Google, primim de la Google doar
                numele, adresa de email și fotografia de profil. Nu avem acces
                la alte date din contul dvs. Google.
              </p>
              <p>
                Folosim aceste date doar pentru a vă crea contul și a vă
                autentifica. Ele sunt păstrate pe serverele cloud Google cât
                timp aveți cont, nu sunt vândute și nu sunt folosite în scopuri
                publicitare. Puteți cere oricând ștergerea lor la{" "}
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
              </p>
              <p>
                Utilizarea informațiilor primite de la API-urile Google respectă{" "}
                <a href="https://developers.google.com/terms/api-services-user-data-policy">
                  Politica Google privind datele utilizatorilor
                </a>
                , inclusiv cerințele de utilizare limitată (Limited Use).
              </p>

              <h3 id="asigurari">2.3. Pentru asigurări</h3>
              <p>
                Informațiile care vor fi colectate sunt acele informații care
                permit ofertarea corectă a unui tip de asigurare, acesta fiind
                principalul scop de prelucrare a datelor personale. Ca scop
                secundar al prelucrării datelor cu caracter personal de către
                Aplicație, avem facilitarea vânzării produselor și serviciilor
                online și buna administrare a acestor produse și servicii,
                însemnând, în principal, contactarea prin email și/sau telefon
                în scopul oferirii consultanței cu privire la serviciile și
                produsele comercializate de către Broker-ul partener, precum și
                evaluarea calității serviciilor noastre în scopul îmbunătățirii
                acestora.
              </p>
              <p>Informațiile colectate sunt:</p>
              <ul>
                <li>numele și prenumele</li>
                <li>locul și data nașterii</li>
                <li>
                  codul numeric personal (CNP) sau echivalentul acestuia în
                  cazul cetățenilor străini
                </li>
                <li>numărul și seria documentului de identitate</li>
                <li>adresa completă a domiciliului/reședinței</li>
                <li>cetățenia</li>
                <li>număr telefon</li>
                <li>
                  diverse alte informații necesare analizei de risc pentru o
                  corectă ofertare a produselor și/sau serviciilor de
                  asigurare
                </li>
              </ul>
              <p>
                În cazul în care în vederea încheierii unui contract de
                asigurare ne transmiteți și datele cu caracter personal ale
                altei persoane, aveți obligația legală de a avea
                consimțământul persoanei respective. De asemenea, aveți
                obligația de a comunica prezenta informare persoanei
                respective în termen de maxim 1 lună de la data furnizării
                datelor cu caracter personal.
              </p>

              <h3 id="auto">
                2.4. Date pe care le colectăm automat atunci când folosiți
                serviciile noastre
              </h3>
              <p>
                Pentru a îmbunătăți serviciile oferite de către aplicație, în
                cazul în care sunteți de acord, aplicația colectează mai multe
                date anonime despre telefonul dumneavoastră și despre modul în
                care folosiți Aplicația. În cazul apariției vreunei erori în
                aplicație, sistemul va colecta automat anumite informații cum
                ar fi tipul telefonului, versiunea sistemului de operare,
                versiunea de aplicație folosită, ora și data la care a apărut
                eroarea și alte statistici.
              </p>

              <h2 id="dece">
                <span className="num">3</span>De ce colectăm aceste date
                personale?
              </h2>
              <ul>
                <li>
                  prestarea serviciilor de asigurare, respectiv generarea și
                  comunicarea ofertei în vederea încheierii contractelor de
                  asigurare, încheierea, modificarea și/sau corectarea precum
                  și reînnoirea acestora
                </li>
                <li>analize, raportări și evaluări interne</li>
                <li>
                  soluționarea reclamațiilor, petițiilor și solicitărilor venite
                  din partea clienților
                </li>
                <li>soluționarea posibilelor litigii</li>
                <li>pentru îmbunătățirea experienței dumneavoastră pe platformă</li>
                <li>
                  pentru a ne asigura că platforma Asistent Auto e sigură și
                  securizată
                </li>
              </ul>

              <h2 id="partajare">
                <span className="num">4</span>Cu cine partajăm datele
                dumneavoastră?
              </h2>
              <p>
                Datele necesare ofertării, emiterii și administrării polițelor
                sunt transmise Broker-ului partener,{" "}
                <strong>INTER Broker de Asigurare SRL</strong> (C.I.F.
                28647300, număr înmatriculare SAR RBK-690/29.08.2011), pe baza
                acordului pe care îl exprimați în aplicație înainte de a primi
                oferte. Puteți citi informarea INTER Broker privind prelucrarea
                datelor cu caracter personal{" "}
                <a href="https://www.euteasigur.ro/prelucrarea-datelor/">
                  aici
                </a>
                .
              </p>
              <p>Datele mai pot fi partajate cu:</p>
              <ul>
                <li>
                  autoritățile statului (inclusiv autorități fiscale, autorități
                  de protecție a consumatorilor precum și cele din domeniul
                  asigurărilor, organele statului competente în materie penală)
                  ca urmare a unei obligații legale a operatorului sau a
                  Broker-ului partener;
                </li>
                <li>
                  furnizorii implicați în mod direct/indirect în procesul de
                  asigurare (ex. asigurători, broker de asigurare, dezvoltatori
                  de servicii IT, prestatori de servicii de regularizare a
                  daunelor, agenți constatatori de daună, furnizori de
                  call-center și asistență în domeniul asigurărilor, clinici
                  medicale partenere și service-uri auto partenere, etc) precum
                  și furnizorii de produse conexe (viniete, asistență rutieră
                  etc);
                </li>
                <li>
                  instituții, agenții de stat, guvernamentale sau asociații din
                  domeniul asigurărilor dacă legislația stipulează acest lucru;
                </li>
                <li>
                  instanțele judecătorești sau arbitrale, notari publici,
                  avocați, executori judecătorești, birouri de traduceri, alte
                  servicii autorizate, experți evaluatori;
                </li>
                <li>furnizorilor de servicii de curierat;</li>
                <li>furnizorilor de servicii de plată/bancare.</li>
              </ul>
              <p>
                Aplicația folosește servicii terțe care s-ar putea să colecteze
                anumite date:
              </p>
              <ul>
                <li>
                  <strong>Firebase Analytics</strong> — este folosit pentru a
                  obține informații legate de modul de folosire a aplicației în
                  scopul îmbunătățirii experienței dumneavoastră. Puteți citi
                  termenii și condițiile acestui serviciu{" "}
                  <a href="https://firebase.google.com/support/privacy">
                    aici
                  </a>
                  .
                </li>
                <li>
                  <strong>Firebase Crashlytics</strong> — este folosit pentru a
                  colecta informații despre erorile care apar în aplicație cu
                  scopul de a le putea rezolva cât mai repede. Puteți citi
                  termenii și condițiile acestui serviciu{" "}
                  <a href="https://firebase.google.com/support/privacy">
                    aici
                  </a>
                  .
                </li>
              </ul>

              <h2 id="securitate">
                <span className="num">5</span>Securitate
              </h2>
              <p>
                Apreciem încrederea dvs. în furnizarea informațiilor dvs.
                personale, astfel ne străduim să folosim mijloace acceptabile
                din punct de vedere comercial pentru a le proteja. Amintiți-vă
                însă că nicio metodă de transmitere pe internet sau metodă de
                stocare electronică nu este 100% sigură și fiabilă și nu putem
                garanta securitatea sa absolută.
              </p>

              <h2 id="linkuri">
                <span className="num">6</span>Linkuri către alte site-uri
              </h2>
              <p>
                Acest Serviciu poate conține legături către alte site-uri. Dacă
                faceți clic pe un link terță parte, veți fi direcționat către
                acel site. Rețineți că aceste site-uri externe nu sunt operate
                de noi. Prin urmare, vă sfătuim să consultați politica de
                confidențialitate a acestor site-uri web. Nu avem control și nu
                ne asumăm responsabilitatea pentru conținutul, politicile de
                confidențialitate sau practicile site-urilor sau serviciilor
                terțe.
              </p>

              <h2 id="stocare">
                <span className="num">7</span>Ce perioadă stocăm datele
                dumneavoastră?
              </h2>
              <p>
                Ca regulă generală, vom stoca datele dumneavoastră cu caracter
                personal cât timp aveți un cont în platforma Asistent Auto.
                Puteți să ne solicitați oricând ștergerea anumitor informații
                sau închiderea contului și vom da curs acestor solicitări sub
                rezerva păstrării anumitor informații inclusiv ulterior
                închiderii contului în situațiile în care legislația aplicabilă
                sau interesele noastre legitime o impun.
              </p>
              <p>
                Datele personale vor fi prelucrate cel puțin pe durata de
                valabilitate a contractului de asigurare, precum și ulterior,
                pe durata necesară executării obligațiilor contractuale de
                către oricare dintre părți. Datele personale vor fi stocate
                atât timp cât este necesar pentru scopurile menționate mai sus
                sau pe perioada de timp prevăzută de dispozițiile legale
                generale precum și cele aplicabile în materie de asigurări.
              </p>
              <p>
                Menționăm faptul că datele dumneavoastră sunt păstrate pe
                servere sigure ale furnizorilor noștri de stocare și arhivare
                date în sisteme care respectă normele de securitate prevăzute
                de GDPR. Puteți citi mai multe informații legate de modul de
                stocare a datelor dumneavoastră pe{" "}
                <a href="https://firebase.google.com/support/privacy">
                  site-ul furnizorului nostru de servicii de cloud
                </a>
                .
              </p>

              <h2 id="drepturi">
                <span className="num">8</span>Drepturile pe care le aveți legat
                de datele dumneavoastră
              </h2>
              <p>
                Conform prevederilor Regulamentului General pentru Protecția
                Datelor Personale, capitolul III Drepturile persoanei vizate,
                art. 12-22, persoanele vizate beneficiază de următoarele
                drepturi:
              </p>

              <h3 id="d1">8.1. Dreptul de a fi informat</h3>
              <p>
                Prin prezenta politică de confidențialitate, utilizatorii
                serviciilor Asistent Auto sunt informați cu privire la
                identitatea și datele de contact ale operatorului, datele de
                contact ale responsabilului cu protecția datelor, scopurile
                prelucrării datelor personale și temeiul juridic al
                prelucrării, categoriile de destinatari ai datelor cu caracter
                personal, transferul către țări terțe sau organizații
                internaționale, perioada de stocare a datelor, precum și
                drepturile pe care le au.
              </p>

              <h3 id="d2">8.2. Dreptul de acces la date</h3>
              <p>
                Utilizatorii au dreptul să obțină din partea noastră o
                confirmare că se prelucrează sau nu date cu caracter personal
                care îi privesc și, în caz afirmativ, acces la datele
                respective și informarea corespunzătoare.
              </p>
              <p>
                La cererea utilizatorilor, putem furniza o copie a datelor cu
                caracter personal care fac obiectul prelucrării. Pentru orice
                alte copii solicitate, putem percepe o taxă rezonabilă, bazată
                pe costurile administrative. Informațiile vor fi furnizate
                într-un format electronic, utilizat în mod curent.
              </p>
              <p>
                Ne rezervăm dreptul de a nu răspunde solicitărilor vădit
                neîntemeiate, excesive sau repetate.
              </p>

              <h3 id="d3">8.3. Dreptul la rectificare</h3>
              <p>
                Utilizatorul poate cere rectificarea datelor cu caracter
                personal inexacte, respectiv completarea datelor cu caracter
                personal care sunt incomplete, inclusiv prin furnizarea unei
                declarații suplimentare. Acest drept poate fi exercitat în mod
                direct prin accesarea contului de utilizator. În cazul în care
                întâmpini dificultăți, te rugăm să ne contactezi.
              </p>

              <h3 id="d4">
                8.4. Dreptul la ștergerea datelor („dreptul de a fi
                uitat&rdquo;)
              </h3>
              <p>
                Utilizatorul poate cere ștergerea datelor cu caracter personal
                care îl privesc, în cazul în care se aplică unul dintre
                următoarele motive:
              </p>
              <ul>
                <li>
                  datele cu caracter personal nu mai sunt necesare pentru
                  îndeplinirea scopurilor pentru care au fost colectate sau
                  prelucrate;
                </li>
                <li>
                  utilizatorul își retrage consimțământul pe baza căruia are
                  loc prelucrarea și nu există niciun alt temei juridic pentru
                  prelucrare;
                </li>
                <li>utilizatorul se opune prelucrării;</li>
                <li>datele cu caracter personal au fost prelucrate ilegal;</li>
                <li>
                  datele cu caracter personal trebuie șterse pentru respectarea
                  unei obligații legale care revine operatorului;
                </li>
                <li>
                  datele cu caracter personal au fost colectate în legătură cu
                  oferirea de servicii ale societății informaționale.
                </li>
              </ul>
              <p>
                Conform dispozițiilor legale, obligația de a șterge datele
                utilizatorului nu se aplică în măsura în care prelucrarea este
                necesară:
              </p>
              <ul>
                <li>
                  pentru exercitarea dreptului la liberă exprimare și la
                  informare;
                </li>
                <li>
                  pentru respectarea unei obligații legale sau pentru
                  îndeplinirea unei sarcini executate în interes public sau în
                  cadrul exercitării unei autorități oficiale cu care este
                  învestit operatorul;
                </li>
                <li>din motive de interes public în domeniul sănătății publice;</li>
                <li>
                  în scopuri de arhivare în interes public, în scopuri de
                  cercetare științifică sau istorică ori în scopuri statistice
                  sau pentru constatarea, exercitarea sau apărarea unui drept
                  în instanță.
                </li>
              </ul>
              <p>
                Utilizatorul își poate exercita acest drept prin trimiterea
                unui email către adresa{" "}
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
                în care să specifice acest lucru.
              </p>

              <h3 id="d5">8.5. Dreptul la restricționarea prelucrării</h3>
              <p>
                Utilizatorul are dreptul de a obține restricționarea
                prelucrării în cazul în care se aplică unul din următoarele
                cazuri:
              </p>
              <ul>
                <li>
                  utilizatorul contestă exactitatea datelor, pentru o perioadă
                  care ne permite să verificăm exactitatea datelor;
                </li>
                <li>
                  prelucrarea este ilegală, iar utilizatorul se opune ștergerii
                  datelor cu caracter personal, solicitând în schimb
                  restricționarea utilizării lor;
                </li>
                <li>
                  nu mai avem nevoie de datele cu caracter personal în scopul
                  prelucrării, dar utilizatorul ni le solicită pentru
                  constatarea, exercitarea sau apărarea unui drept în instanță;
                </li>
                <li>
                  utilizatorul s-a opus prelucrării, pentru intervalul de timp
                  în care se verifică dacă drepturile legitime ale operatorului
                  prevalează asupra celor ale persoanei vizate.
                </li>
              </ul>
              <p>
                Utilizatorul își poate exercita acest drept prin trimiterea
                unui email către adresa{" "}
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>{" "}
                în care să specifice acest lucru.
              </p>

              <h3 id="d6">8.6. Dreptul la portabilitatea datelor</h3>
              <p>
                Utilizatorul are dreptul de a primi datele cu caracter personal
                care îl privesc și pe care le-a furnizat operatorului într-un
                format structurat, utilizat în mod curent și care poate fi
                citit automat și are dreptul de a transmite aceste date altui
                operator, fără obstacole din partea operatorului căruia i-au
                fost furnizate datele cu caracter personal, în cazul în care:
              </p>
              <ul>
                <li>prelucrarea se bazează pe consimțământ sau pe un contract și</li>
                <li>prelucrarea este efectuată prin mijloace automate.</li>
              </ul>
              <p>
                În exercitarea dreptului său la portabilitatea datelor,
                utilizatorul are dreptul ca datele cu caracter personal să fie
                transmise direct de la un operator la altul acolo unde acest
                lucru este posibil din punct de vedere tehnic.
              </p>

              <h3 id="d7">8.7. Dreptul de opoziție</h3>
              <p>
                Utilizatorul are dreptul de a se opune în orice moment, din
                motive întemeiate și legitime legate de situația sa
                particulară, ca date care îl vizează să facă obiectul unei
                prelucrări, cu excepția cazurilor în care există dispoziții
                legale contrare. În caz de opoziție justificată, prelucrarea nu
                mai poate viza datele în cauză.
              </p>

              <h3 id="d8">
                8.8. Dreptul de a nu fi supus unui proces decizional individual
                automatizat
              </h3>
              <p>
                Utilizatorii au dreptul de a cere și de a obține
                retragerea/anularea/reevaluarea oricărei decizii care produce
                efecte juridice în privința sa, adoptată exclusiv pe baza unei
                prelucrări de date cu caracter personal, efectuată prin
                mijloace automate, destinată să evalueze unele aspecte ale
                personalității sale, precum competența profesională,
                credibilitatea, comportamentul sau ori alte asemenea aspecte.
              </p>

              <h3 id="d9">8.9. Dreptul de a se adresa justiției</h3>
              <p>
                Fără a se aduce atingere posibilității de a se adresa cu
                plângere autorității de supraveghere privind protecția datelor
                personale, utilizatorii au dreptul de a se adresa justiției
                pentru apărarea oricăror drepturi garantate de lege, care le-au
                fost încălcate.
              </p>
              <p>
                Dacă utilizatorul a suferit un prejudiciu în urma unei
                prelucrări de date cu caracter personal, efectuată ilegal, se
                poate adresa instanței competente pentru repararea acestuia.
              </p>

              <h2 id="modificari">
                <span className="num">9</span>Modificarea politicii de
                confidențialitate
              </h2>
              <p>
                De fiecare dată când modificăm politica de confidențialitate
                veți fi informat și puteți alege în orice moment dacă doriți să
                utilizați în continuare serviciile noastre.
              </p>
              <p>
                Noua politică de confidențialitate va intra în vigoare la data
                publicării pe asistent-auto.ro.
              </p>
              <p>
                Politica de confidențialitate face parte din{" "}
                <Link to="/termeni">termenii și condițiile</Link> de utilizare
                a serviciilor Asistent Auto.
              </p>

              <div className="legal-meta">
                <span>
                  <b>Ultima actualizare:</b> 01.10.2026
                </span>
                <span>
                  <b>Contact DPO:</b>{" "}
                  <a href={`mailto:${contactEmail}`} style={{ color: "inherit" }}>
                    {contactEmail}
                  </a>
                </span>
              </div>

              <Link className="legal-cross" to="/termeni">
                <span>
                  Vezi și termenii care guvernează utilizarea serviciilor
                  Asistent Auto.
                </span>
                <span className="legal-cross-link">
                  Termeni și condiții
                  <Icon name="arrow_forward" />
                </span>
              </Link>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
